import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Paper,
    Grid,
    IconButton,
    Collapse,
    Button,
    Divider,
    Chip,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Alert
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    CalendarToday as CalendarIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
    DiaryCategory,
    DiaryItemStatus,
    getCategoryLabel,
    getStatusLabel,
    getCategoryOptions,
    getStatusOptions
} from "../../models/diaryTypes";
import { DiaryView } from './DiaryEntriesList';
import { startOfDayUtc, endOfDayUtc, addDays } from "../../functions/dateFormatter";

import ViewFilterSelector, { FilterItem, ViewFilterMode, LayoutMode, defaultViewFilters, FilterSelectorHandle } from './ViewFilterSelector';
import './ViewFilterSelectorStyleSheet.css';

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../styles/mui_theme";

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Filter interface
interface DiaryFilters {
    category?: DiaryCategory | '';
    status?: DiaryItemStatus | '';
    dateRange?: {
        start?: Date | null | undefined;
        end?: Date | null | undefined;
    };
    viewFilters: FilterItem[];
}

// Props for filter component
interface DiaryFilterControlsProps {
    filters: DiaryFilters;
    onFiltersChange: (filters: DiaryFilters) => void;
    viewMode: DiaryView;
    onViewModeChange: (viewMode: DiaryView) => void;
    showFilters?: boolean;
    onShowFiltersChange?: (show: boolean) => void;
}

interface DateValidation {
    hasError: boolean;
    message: string;
    type: 'invalid-range' | 'future-start' | 'past-end' | '' | null;
}


// Enhanced filter controls component
const DiaryFilterControl: React.FC<DiaryFilterControlsProps> = ({
    filters,
    onFiltersChange,
    viewMode,
    onViewModeChange,
    showFilters = true,
    onShowFiltersChange
}) => {

    const filterSelectorRef = useRef<FilterSelectorHandle>(null);

    const [localFilters, setLocalFilters] = useState<DiaryFilters>(filters);
    const [expanded, setExpanded] = useState(false);
    const [viewFilterMode, setViewFilterMode] = useState<ViewFilterMode>('switch');
    const [layoutMode, setLayoutMode] = useState<LayoutMode>('auto');

    const [externalFilterState, setExternalFilterState] = useState({
        tasks: true,
        events: true,
        series: false,
        linked: false
    });

    const tasksFilter = localFilters.viewFilters.find(f => f.id === 'tasks');
    const eventsFilter = localFilters.viewFilters.find(f => f.id === 'events');
    const seriesFilter = localFilters.viewFilters.find(f => f.id === 'series');
    const linkedFilter = localFilters.viewFilters.find(f => f.id === 'linked-items');

    // External control functions
    const toggleFilterFromExternal = (filterId: string) => {
        filterSelectorRef.current?.toggleFilter(filterId);
        setExternalFilterState(prev => ({
            ...prev,
            [filterId]: !prev[filterId as keyof typeof prev]
        }));
    };

    const setFilterFromExternal = (filterId: string, checked: boolean) => {
        filterSelectorRef.current?.setFilter(filterId, checked);
        setExternalFilterState(prev => ({
            ...prev,
            [filterId]: checked
        }));
    };

    const resetFilters = () => {
        filterSelectorRef.current?.resetFilters();
        setExternalFilterState({
            tasks: true,
            events: true,
            series: true,
            linked: true
        });
    };

    // Update local filters when props change
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const createCategoryChangeEvent = (value: '' | DiaryCategory): SelectChangeEvent<'' | DiaryCategory> => {
        return {
            target: {
                value,
                name: 'category'
            }
        } as SelectChangeEvent<'' | DiaryCategory>;
    };

    const createStatusChangeEvent = (value: '' | DiaryItemStatus): SelectChangeEvent<'' | DiaryItemStatus> => {
        return {
            target: {
                value,
                name: 'status'
            }
        } as SelectChangeEvent<'' | DiaryItemStatus>;
    };

    const handleCategoryChange = (event: SelectChangeEvent<DiaryCategory | ''>) => {
        const newFilters = {
            ...localFilters,
            category: event.target.value as DiaryCategory | ''
        };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleStatusChange = (event: SelectChangeEvent<DiaryItemStatus | ''>) => {
        const newFilters = {
            ...localFilters,
            status: event.target.value as DiaryItemStatus | ''
        };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleDateRangeChange = (type: 'start' | 'end', date: Date | null) => {
        const newDateRange = {
            ...localFilters.dateRange,
            [type]: date
        };
        const newFilters = {
            ...localFilters,
            dateRange: newDateRange
        };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleViewFilterChange = (viewfiltersChange: FilterItem[]) => {
        const newFilters = {
            ...localFilters,
            viewFilters: viewfiltersChange
        };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleViewModeChange = (event: SelectChangeEvent<DiaryView>) => {
        onViewModeChange(event.target.value as DiaryView);
    };

    const handleViewModeIconChange = (
        event: React.MouseEvent<HTMLElement>,
        newViewMode: DiaryView | null
    ) => {
        if (newViewMode !== null) {
            onViewModeChange(newViewMode);
        }
    };

    const clearFilters = () => {
        const clearedFilters: DiaryFilters = {
            category: DiaryCategory.All,
            status: DiaryItemStatus.All,
            dateRange: { start: null, end: null },
            viewFilters: defaultViewFilters,
        };
        setLocalFilters(clearedFilters);
        onFiltersChange(clearedFilters);
        resetFilters();
    };

    const applyQuickFilter = (days: number) => {

        const futureScope = days > 0;

        const start = addDays(startOfDayUtc(new Date()), futureScope ? 0 : days);
        const end = addDays(endOfDayUtc(new Date()), futureScope ? days : 0);

        const newFilters = {
            ...localFilters,
            dateRange: { start, end }
        };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const hasActiveFilters =
        localFilters.category !== DiaryCategory.All ||
        localFilters.status !== DiaryItemStatus.All ||
        localFilters.dateRange?.start !== null ||
        localFilters.dateRange?.end !== null ||
        tasksFilter?.checked ||
        eventsFilter?.checked ||
        seriesFilter?.checked ||
        linkedFilter?.checked ;


    const activeFilterCount = [
        localFilters.category !== DiaryCategory.All ? 1 : 0,
        localFilters.status !== DiaryItemStatus.All ? 1 : 0,
        localFilters.dateRange?.start !== null ? 1 : 0,
        localFilters.dateRange?.end !== null ? 1 : 0,
        tasksFilter?.checked ? 1 : 0,
        eventsFilter?.checked ? 1 : 0,
        seriesFilter?.checked ? 1 : 0,
        linkedFilter?.checked ? 1 : 0,
    ].filter(Boolean).length;

    // Format date for display
    const formatDate = (dateParm: Date | null): string => {
        if (!dateParm) return '';
        const formattedDate = dayjs(dateParm).format("DD MMM YYYY");
    return formattedDate;
    };

    const [dateValidation, setDateValidation] = useState<DateValidation>({
        hasError: false,
        message: '',
        type: null
    });

    // Enhanced validation function
    const validateDateRangeAdvanced = (
        start: Dayjs | null,
        end: Dayjs | null
    ): DateValidation => {
        const today = dayjs().startOf('day');

        if (!start && !end) {
            return { hasError: false, message: '', type: null };
        }

        // Check if start date is in the future (if needed)
        if (start && start.isAfter(today, 'day')) {
            // This might be valid depending on your use case
            // Remove or modify based on requirements
        }

        // Check if end date is in the past (if needed)
        if (end && end.isBefore(today, 'day')) {
            // This might be valid depending on your use case
            // Remove or modify based on requirements
        }

        // Check date ordering
        if (start && end) {
            if (start.isAfter(end, 'day')) {
                return {
                    hasError: true,
                    message: 'Start date must be before or equal to end date',
                    type: 'invalid-range'
                };
            }

            // Check if date range is too large (optional)
            const daysDiff = end.diff(start, 'day');
            if (daysDiff > 365) {
                return {
                    hasError: true,
                    message: 'Date range cannot exceed 1 year',
                    type: 'invalid-range'
                };
            }
        }

        return { hasError: false, message: '', type: null };
    };

    // Enhanced handleDateRangeChange with auto-correction
    const handleDateRangeChangeEnhanced = (type: 'start' | 'end', date: Dayjs | null) => {
        let newStart = type === 'start' ? date : dayjs(localFilters.dateRange?.start) || null;
        let newEnd = type === 'end' ? date : dayjs(localFilters.dateRange?.end) || null;

        const validation = validateDateRangeAdvanced(newStart, newEnd);
        setDateValidation(validation);

        if (validation.hasError && validation.type === 'invalid-range') {
            // Auto-correct invalid ranges
            if (type === 'start' && date && newEnd && date.isAfter(newEnd, 'day')) {
                newEnd = date;
            } else if (type === 'end' && date && newStart && date.isBefore(newStart, 'day')) {
                newStart = date;
            }

            // Re-validate after correction
            const correctedValidation = validateDateRangeAdvanced(newStart, newEnd);
            setDateValidation(correctedValidation);
        }

        const newDateRange = {
            start: dayjs(newStart).toDate(),
            end: dayjs(newEnd).toDate(),
        };

        const newFilters = {
            ...localFilters,
            dateRange: newDateRange
        };

        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    return (
        <ThemeProvider theme={theme}>
        <Paper sx={{ p: 1, mb: 3, borderRadius: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1
                    }}
                >
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <FilterListIcon />
                    Filters & View
                </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                        }}
                    >
                    {hasActiveFilters && (
                        <Chip
                            label={`${activeFilterCount} active filter${activeFilterCount > 1 ? 's' : ''}`}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}

                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        aria-label={expanded ? 'Collapse filters' : 'Expand filters'}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Basic controls always visible */}
            <Grid container spacing={2}>
                <Grid sx={{ xs: 12, sm: 6, md: 3, lg:3 }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: 'repeat(3, minmax(0, 1fr))'
                            },
                            gap: 1.5
                        }}>
                            <Typography variant="subtitle1">View Mode:</Typography>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeIconChange}
                                aria-label="view mode"
                                size="small"
                            >
                                <ToggleButton value="grouped" aria-label="grouped view">
                                    <ViewComfyIcon sx={{ mr: 1 }} />
                                    Grouped
                                </ToggleButton>
                                <ToggleButton value="list" aria-label="list view">
                                    <ViewListIcon sx={{ mr: 1 }} />
                                    List
                                </ToggleButton>
                                <ToggleButton value="compact" aria-label="compact view">
                                    <ViewModuleIcon sx={{ mr: 1 }} />
                                    Compact
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                </Grid>

                <Grid sx={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                        <InputLabel id="category-filter-label" shrink={localFilters.category !== ''}>Category</InputLabel>
                        <Select
                            labelId="category-filter-label"
                            value={localFilters.category}
                            label="Category"
                            onChange={handleCategoryChange}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return <Box sx={{ opacity: 0.7 }}>All</Box>;
                                return getCategoryLabel(selected);
                            }}
                            sx={{
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '40px'
                                }
                            }}
                        >
                            {getCategoryOptions().map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid sx={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                        <InputLabel id="status-filter-label" shrink={localFilters.status !== ''}>Status</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            value={localFilters.status || ''}
                            label="Status"
                            onChange={handleStatusChange}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return <Box sx={{ opacity: 0.7 }}>All</Box>;
                                return getStatusLabel(selected);
                            }}
                            sx={{
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '40px'
                                }
                            }}
                        >
                            {getStatusOptions().map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid sx={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%', minWidth: { xs: '100%', sm: 140 } }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={clearFilters}
                            disabled={!hasActiveFilters}
                            startIcon={<ClearIcon />}
                            sx={{ flex: 1, minWidth: { xs: '100%', sm: 120 } }}
                        >
                            Clear Filters
                        </Button>
                    </Box>
                </Grid>
                <Grid sx={{ xs: 12, lg: 1 }}>
                    {/* Empty space for alignment on large screens */}
                </Grid>
            </Grid>

            {/* Expanded advanced filters */}
            <Collapse in={expanded}>
                <Divider sx={{ my: 2 }} />
                <ViewFilterSelector
                    ref={filterSelectorRef}
                    layoutMode={layoutMode}
                    defaultViewMode={viewFilterMode}
                    onFilterChange={handleViewFilterChange}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                    Date Range Filter
                </Typography>

                <Grid container spacing={2}>
                    <Grid sx={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="Start Date"
                            value={dayjs(localFilters.dateRange?.start)}
                            onChange={(date) => handleDateRangeChangeEnhanced('start', date)}
                            maxDate={dayjs(localFilters.dateRange?.end) || undefined}
                            shouldDisableDate={(date) => {
                                // Disable future dates
                                // return date.isAfter(dayjs().add(1, 'year'), 'day');
                                return false;
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: dateValidation.hasError,
                                    helperText: dateValidation.type === 'invalid-range' ? ' ' : 'Earliest date',
                                    FormHelperTextProps: {
                                        sx: { height: dateValidation.hasError ? 0 : 'auto' }
                                    },
                                    InputProps: {
                                        startAdornment: <CalendarIcon sx={{ mr: 1, color: dateValidation.hasError ? 'error.main' : 'action.active' }} />,
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: dateValidation.hasError ? 'error.main' : undefined
                                            }
                                        }
                                    }
                                },
                                actionBar: {
                                    actions: ['clear', 'today']
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-error fieldset': {
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid sx={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="End Date"
                            value={dayjs(localFilters.dateRange?.end)}
                            onChange={(date) => handleDateRangeChangeEnhanced('end', date)}
                            minDate={dayjs(localFilters.dateRange?.start) || undefined}
                            shouldDisableDate={(date) => {
                                // Disable dates before start date
                                if (localFilters.dateRange?.start) {
                                    return date.isBefore(localFilters.dateRange.start, 'day');
                                }
                                return false;
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: dateValidation.hasError,
                                    helperText: dateValidation.type === 'invalid-range' ? ' ' : 'Latest date',
                                    FormHelperTextProps: {
                                        sx: { height: dateValidation.hasError ? 0 : 'auto' }
                                    },
                                    InputProps: {
                                        startAdornment: <CalendarIcon sx={{ mr: 1, color: dateValidation.hasError ? 'error.main' : 'action.active' }} />,
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: dateValidation.hasError ? 'error.main' : undefined
                                            }
                                        }
                                    }
                                },
                                actionBar: {
                                    actions: ['clear', 'today']
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-error fieldset': {
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                {dateValidation.hasError && (
                    <Alert
                        severity="error"
                        sx={{
                            mt: 1,
                            '& .MuiAlert-icon': { alignItems: 'center' }
                        }}
                        onClose={() => setDateValidation({ hasError: false, message: '', type: null })}
                    >
                        <Typography variant="body2">
                            {dateValidation.message}
                        </Typography>
                        {dateValidation.type === 'invalid-range' && (
                            <Button
                                size="small"
                                sx={{ mt: 0.5 }}
                                onClick={() => {
                                    // Auto-fix button
                                    if (localFilters.dateRange?.start && localFilters.dateRange?.end) {
                                        const newDateRange = dayjs(localFilters.dateRange.start).isAfter(localFilters.dateRange.end, 'day')
                                            ? { start: localFilters.dateRange.end, end: localFilters.dateRange.start }
                                            : localFilters.dateRange;

                                        const newFilters = {
                                            ...localFilters,
                                            dateRange: newDateRange
                                        };

                                        setLocalFilters(newFilters);
                                        onFiltersChange(newFilters);
                                        setDateValidation({ hasError: false, message: '', type: null });
                                    }
                                }}
                            >
                                Swap dates to fix
                            </Button>
                        )}
                    </Alert>
                )}

                    {/* Quick date filters */}
                <div className="quickFilters-section">
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Quick Filters
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(-90)}
                        sx={{ borderRadius: 20 }}
                    >
                        Last 90 Days
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(-30)}
                        sx={{ borderRadius: 20 }}
                    >
                        Last 30 Days
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(-7)}
                        sx={{ borderRadius: 20 }}
                    >
                        Last 7 Days
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(0)}
                        sx={{ borderRadius: 20 }}
                    >
                        Today Only
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(7)}
                        sx={{ borderRadius: 20 }}
                    >
                        Next 7 Days
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(30)}
                        sx={{ borderRadius: 20 }}
                    >
                        Next 30 Days
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => applyQuickFilter(90)}
                        sx={{ borderRadius: 20 }}
                    >
                        Next 90 Days
                    </Button>
                </Stack>
                </div>
                {/* Active filters display */}
                {hasActiveFilters && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            Active Filters
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {localFilters.category !== DiaryCategory.All && localFilters.category !== '' && (
                                <Chip
                                    label={`Category: ${getCategoryLabel(localFilters.category)}`}
                                    onDelete={() => handleCategoryChange(createCategoryChangeEvent(DiaryCategory.All))}
                                    size="small"
                                    variant="outlined"
                                />
                            )}

                            {localFilters.status !== DiaryItemStatus.All && localFilters.status !== '' && (
                                <Chip
                                    label={`Status: ${getStatusLabel(localFilters.status)}`}
                                    onDelete={() => handleStatusChange(createStatusChangeEvent(DiaryItemStatus.All))}
                                    size="small"
                                    variant="outlined"
                                />
                            )}

                            {localFilters.dateRange?.start && (
                                <Chip
                                    label={`From: ${formatDate(localFilters.dateRange.start)}`}
                                    onDelete={() => handleDateRangeChange('start', null)}
                                    size="small"
                                    variant="outlined"
                                />
                            )}

                            {localFilters.dateRange?.end && (
                                <Chip
                                    label={`To: ${formatDate(localFilters.dateRange.end)}`}
                                    onDelete={() => handleDateRangeChange('end', null)}
                                    size="small"
                                    variant="outlined"
                                />
                            )}

                            {localFilters.viewFilters
                                .filter(filter => filter.checked)
                                .map(filter => (
                                    <Chip
                                        key={filter.id}
                                        label={`${filter.label}`}
                                        onDelete={() => setFilterFromExternal(filter.id, false)}
                                        size="small"
                                        variant="outlined"
                                        style={{
                                            backgroundColor: `${filter.color}15`, // Light background with opacity
                                            borderColor: filter.color,
                                            color: filter.color
                                        }}
                                    />
                                ))}

                        </Stack>
                    </>
                )}
            </Collapse>
            </Paper>
        </ThemeProvider>
    );
};

export type { DiaryFilters, DiaryFilterControlsProps };
export default DiaryFilterControl;